import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Grid,
    Input,
    Select,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    HStack,
    Badge,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Switch,
    useDisclosure,
    AlertDialogOverlay,
    AlertDialog,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogBody,
    AlertDialogFooter,
    Center
} from '@chakra-ui/react';
import {
    FiUsers,
    FiTarget,
    FiTrendingUp,
    FiPlus,
    FiSearch,
    FiBarChart,
    FiRefreshCw,
    FiChevronUp,
    FiChevronDown,
    FiPlay
} from 'react-icons/fi';

import Pagination from 'react-js-pagination';
import Header from '../../components/Header';
import { useNavigate } from 'react-router-dom';
import { EditIcon } from '@chakra-ui/icons';
import { BsEye } from 'react-icons/bs';
import CustomBadge from '../../components/SharedComponent/Badge';
import { LuFileText, LuPieChart } from 'react-icons/lu';
import moment from 'moment';
import { getUserTimeZone } from '../../utils/DefaultFunction';
import usePagePermissions from '../../hooks/usePagePermissions';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
// LOCAL ONLY — AI Agents integration (inline accordion, no popup)
import AISuggestionsAccordion from '../../components/AIAgents/AISuggestionsAccordion';
import BusinessGoalModal from '../../components/AIAgents/BusinessGoalModal';
import PageHeading from '../../components/shared/PageHeading';

const Cohort = ({ onCreateCohort, onEditCohort, header }) => {
    const [cohorts, setCohorts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [sortBy, setSortBy] = useState('updated_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // LOCAL ONLY — AI Agents integration. Clicking "Recommend with AI"
    // opens the BusinessGoalModal; on Generate we close the modal and
    // dispatch the accordion run with the chosen goal.
    const aiAccordionRef = React.useRef(null);
    const [aiGoalModalOpen, setAiGoalModalOpen] = useState(false);
    const toast = useToast();
    const navigate = useNavigate()
    // Stats data (separate from paginated data)
    const [statsData, setStatsData] = useState({});
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState({});

    const [selectedCohort, setSelectedCohort] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = React.useRef();
    const [isTriggering, setIsTriggering] = useState(false);

    const { loading: permissionsLoading, can } = usePagePermissions('marketing/cohorts');

    const listPermission = can("cohort-list")
    // RP_007: Create also requires cohort-list, consistent with every other module.
    const createPermission = listPermission && can("cohort-create")
    const editPermission = can("cohort-edit")
    const viewPermission = can("cohort-view")
    const changeStatusPermission = can("cohort-change-status")
    const reportPermission = can("cohort-report")
    const pausePermission = can("cohort-play-pause")

    // API Headers
    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem('api-key'),
        "x-access-token": localStorage.getItem('customer-portal'),
    });

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (permissionsLoading || !listPermission) return;
        if (currentPage !== 1) {
            setCurrentPage(1);
        } else {
            fetchCohorts();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsLoading, listPermission, debouncedSearchTerm, statusFilter, sortBy, sortOrder, limit]);

    // Fetch cohorts when page changes
    useEffect(() => {
        if (permissionsLoading || !listPermission) return;
        fetchCohorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsLoading, listPermission, currentPage]);

    // Load initial data
    useEffect(() => {
        if (permissionsLoading || !listPermission) return;
        fetchCohorts();
        fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsLoading, listPermission]);

    const fetchCohorts = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: limit.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
            });

            if (debouncedSearchTerm) {
                params.append('search', debouncedSearchTerm);
            }

            if (statusFilter && statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            const url = `${process.env.REACT_APP_COHORT}/cohorts?${params.toString()}`;


            const response = await fetch(url, {
                method: 'GET',
                headers: getApiHeaders()
            });

            const data = await response.json();

            if (data.status) {
                // Map API response to component format
                const mappedCohorts = data.data.cohorts.map((cohort) => ({
                    id: cohort._id,
                    name: cohort.name,
                    description: cohort.description || '',
                    customerCount: cohort.user_count || 0,
                    status: cohort.status === 'active' ? 'Active' : 'Inactive',
                    type: 'Manual',
                    refreshRate: cohort.refresh_frequency
                        ? cohort.refresh_frequency.charAt(0).toUpperCase() + cohort.refresh_frequency.slice(1)
                        : 'Daily',
                    createdBy: cohort?.created_by,
                    lastCalculatedAt: cohort.last_calculated_at,
                    nextCalculationAt: cohort.next_calculation_at,
                    createdAt: cohort.created_at,
                    updatedAt: cohort.updated_at
                }));

                setCohorts(mappedCohorts);
                setTotalCount(data.data.pagination.total_count);
                setTotalPages(data.data.pagination.total_pages);

                // LOCAL ONLY — reconcile AI suggestions: if a cohort matching
                // any pending suggestion exists in the DB, flip that suggestion
                // to "accepted". Idempotent + cheap (name match).
                try {
                    const { reconcileWithSaved } = require('../../components/AIAgents/aiSuggestionsStore');
                    const { getActiveCustomerId } = require('../../components/AIAgents/aiAgentsClient');
                    reconcileWithSaved('cohort', getActiveCustomerId(), data.data.cohorts || []);
                } catch (e) { /* non-fatal */ }


            } else {
                setError(data.message || 'Failed to fetch cohorts');
            }
        } catch (error) {
            console.error('Error fetching cohorts:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch cohorts';
            setError(errorMessage);

            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAnalytics = async () => {
        setLoadingAnalytics(true);
        try {
            const url = `${process.env.REACT_APP_COHORT}/cohorts/analytics`;
            const response = await fetch(url, {
                method: 'GET',
                headers: getApiHeaders()
            });

            const data = await response.json();

            if (data.status) {
                setStatsData(data.data);

            } else {
                console.error('Failed to fetch analytics:', data.message);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoadingAnalytics(false);
        }
    };

    // Update Cohort Status
    const updateCohortStatus = async (cohortId, newStatus) => {
        setUpdatingStatus(prev => ({ ...prev, [cohortId]: true }));

        try {
            const url = `${process.env.REACT_APP_COHORT}/cohorts/${cohortId}/status`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    ...getApiHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: newStatus
                })
            });

            const data = await response.json();

            if (data.status) {
                // Update the cohort in the local state
                setCohorts(cohorts.map(cohort =>
                    cohort.id === cohortId
                        ? { ...cohort, status: newStatus === 'active' ? 'Active' : 'Inactive' }
                        : cohort
                ));

                // Refresh analytics to get updated stats
                fetchAnalytics();

                toast({
                    title: "Success",
                    description: `Cohort status updated to ${newStatus}`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Error",
                    description: data.message || "Failed to update cohort status",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });

                // Revert the change if API failed
                // This will cause the switch to go back to its original position
                setTimeout(() => {
                    fetchCohorts();
                }, 100);
            }
        } catch (error) {
            console.error('Error updating cohort status:', error);
            toast({
                title: "Error",
                description: "Failed to update cohort status",
                status: "error",
                duration: 3000,
                isClosable: true,
            });

            // Revert the change if request failed
            setTimeout(() => {
                fetchCohorts();
            }, 100);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [cohortId]: false }));
        }
    };

    // Handle Status Toggle
    const handleStatusToggle = async (cohort) => {
        const newStatus = cohort.status === 'Active' ? 'inactive' : 'active';
        await updateCohortStatus(cohort.id, newStatus);
    };


    // Fixed handlePageChange for react-js-pagination
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return null;
        return sortOrder === 'asc' ? <FiChevronUp /> : <FiChevronDown />;
    };

    const handleCreateNew = () => {
        navigate("/cohorts/create");

    };

    const handleEdit = (cohort) => {
        navigate(`/cohort/edit/${cohort.id}`)
    };
    const handleNavigateReport = (cohort) => {
        navigate(`/cohort-report/${cohort.id}?name=${cohort?.name}`)
    };

    const handleRetry = () => {
        fetchCohorts();
    };



    // Error state
    if (error && cohorts.length === 0) {
        return (
            <Box p={6} maxW="100%" mx="auto">
                <Alert status="error" mb={4}>
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold">Error loading cohorts</Text>
                        <Text fontSize="sm">{error}</Text>
                    </Box>
                </Alert>
                <Box textAlign="center" py={10}>
                    <Button
                        leftIcon={<FiRefreshCw />}
                        colorScheme="blue"
                        onClick={handleRetry}
                    >
                        Retry Loading
                    </Button>
                </Box>
            </Box>
        );
    }

    const handleViewCohort = (cohort) => {
        navigate(`/cohorts/detail/${cohort.id}`)
    }


    const handleTriggerCohort = async () => {
        if (!selectedCohort) return;
        setIsTriggering(true);

        try {
            const url = `${process.env.REACT_APP_COHORT}/cohorts/${selectedCohort.id}/trigger`;
            const response = await fetch(url, {
                method: "POST",
                headers: getApiHeaders(),
            });

            const data = await response.json();

            if (data.status) {
                toast({
                    title: "Cohort Triggered",
                    description: `${selectedCohort.name} is now being refreshed.`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                fetchCohorts(); // Refresh list
            } else {
                toast({
                    title: "Error",
                    description: data.message || "Failed to trigger cohort.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "An error occurred while triggering the cohort.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsTriggering(false);
            onClose();
        }
    };


    if (permissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper >
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }
    return (
        <>
            <Header header={header} />
            <BoxWrapper>

                <PageHeading icon={LuPieChart} title="Segments Analysis" subtitle="Build dynamic user segments using events and features" mb={8}>
                    <Button
                        leftIcon={<FiRefreshCw />}
                        variant="ghost"
                        onClick={handleRetry}
                        size="sm"
                        isLoading={isLoading}
                    >
                        Refresh
                    </Button>
                    {createPermission && (
                        <>
                            {/* LOCAL ONLY — AI Agents integration (no popup) */}
                            <Button
                                leftIcon={<span style={{ fontSize: 14 }}>✨</span>}
                                onClick={() => setAiGoalModalOpen(true)}
                                size="sm"
                                bgGradient="linear(to-r, purple.500, blue.500)"
                                color="white"
                                _hover={{ bgGradient: "linear(to-r, purple.600, blue.600)" }}
                            >
                                Recommend with AI
                            </Button>
                            <BusinessGoalModal
                                isOpen={aiGoalModalOpen}
                                onClose={() => setAiGoalModalOpen(false)}
                                onConfirm={(goal) => {
                                    setAiGoalModalOpen(false);
                                    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
                                    aiAccordionRef.current?.triggerRun({ business_goal: goal });
                                }}
                            />

                            <Button
                                leftIcon={<FiPlus />}
                                variant="outline"
                                onClick={handleCreateNew}
                                size={"sm"}
                            >
                                Create Segment
                            </Button>
                        </>
                    )}
                </PageHeading>
                {listPermission ? <>
                    {/* LOCAL ONLY — AI Segment Suggestions (driven by ref) */}
                    {createPermission && (
                    <AISuggestionsAccordion
                        ref={aiAccordionRef}
                        type="cohort"
                        onUse={(s) => {
                            try {
                                sessionStorage.setItem("ai-cohort-prefill", JSON.stringify({
                                    name: s.name,
                                    description: s.description,
                                    refresh_frequency: s.refresh_frequency || "daily",
                                    canonical: s.canonical
                                }));
                            } catch {}
                            navigate("/cohorts/create");
                        }}
                    />
                    )}
                    {/* Stats Grid */}
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6} mb={8}>
                        <Box className='box-style-card'>
                            <Box>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Total Segments</StatLabel>
                                        <Box color="blue.500" mr={2}>
                                            <FiUsers />
                                        </Box>

                                    </Flex>
                                    <StatNumber>
                                        {loadingAnalytics ? <Spinner size="sm" /> : statsData?.total_cohorts?.count}
                                    </StatNumber>
                                    <StatHelpText>{statsData?.total_cohorts?.active} active</StatHelpText>
                                </Stat>
                            </Box>
                        </Box>

                        <Box className='box-style-card'>
                            <Box>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Total Customers</StatLabel>
                                        <Box color="green.500" mr={2}>
                                            <FiTarget />
                                        </Box>

                                    </Flex>
                                    <StatNumber>
                                        {loadingAnalytics ? <Spinner size="sm" /> : statsData?.total_customers?.count?.toLocaleString()}
                                    </StatNumber>
                                    <StatHelpText>{statsData?.total_customers?.description}</StatHelpText>
                                </Stat>
                            </Box>
                        </Box>

                        <Box className='box-style-card'>
                            <Box>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Active Segments</StatLabel>

                                        <Box color="purple.500" mr={2}>
                                            <FiTrendingUp />
                                        </Box>
                                    </Flex>
                                    <StatNumber>
                                        {loadingAnalytics ? <Spinner size="sm" /> : statsData?.active_cohorts?.count}
                                    </StatNumber>
                                    <StatHelpText>{statsData?.active_cohorts?.description}</StatHelpText>
                                </Stat>
                            </Box>
                        </Box>

                        <Box className='box-style-card'>
                            <Box>
                                <Stat>
                                    <Flex align="center" mb={2} justifyContent={'space-between'}>
                                        <StatLabel>Largest Segment</StatLabel>

                                        <Box color="orange.500" mr={2}>
                                            <FiBarChart />
                                        </Box>
                                    </Flex>
                                    <StatNumber>
                                        {loadingAnalytics ? <Spinner size="sm" /> : statsData?.largest_cohort?.count?.toLocaleString()}
                                    </StatNumber>
                                    <StatHelpText>{statsData?.largest_cohort?.description}</StatHelpText>
                                </Stat>
                            </Box>
                        </Box>
                    </Grid>


                    {/* Cohort Management */}
                    <Box className='box-style-card'>
                        <Box>
                            <Flex justify="space-between" align="center">
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="2xl" fontWeight={600}>Segment Management</Text>
                                    <Text color="gray.600">View and manage your customer segments</Text>
                                </VStack>

                            </Flex>
                        </Box>
                        <Box position="relative">
                            {/* Filters and Search */}
                            <Grid templateColumns="2fr 1fr 1fr 1fr" gap={4} mb={6}>
                                <Box position="relative">
                                    <Input
                                        placeholder="Search segments..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        pl={10}
                                    />
                                    <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                                        <FiSearch />
                                    </Box>
                                    {isLoading && searchTerm && (
                                        <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                                            <Spinner size="sm" />
                                        </Box>
                                    )}
                                </Box>

                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    placeholder="All Status"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </Select>

                                <Select
                                    value={limit}
                                    onChange={(e) => setLimit(Number(e.target.value))}
                                >
                                    <option value={5}>5 per page</option>
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                </Select>

                                <Text fontSize="sm" color="gray.600" alignSelf="center">
                                    Page {currentPage} of {totalPages}
                                </Text>
                            </Grid>

                            {/* Table */}
                            <TableContainer>
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th
                                                cursor="pointer"
                                                onClick={() => handleSort('name')}
                                                _hover={{ bg: 'gray.50' }}
                                            >
                                                <HStack>
                                                    <Text>Segment Name</Text>
                                                    {getSortIcon('name')}
                                                </HStack>
                                            </Th>
                                            <Th>Description</Th>
                                            <Th
                                                cursor="pointer"
                                                onClick={() => handleSort('user_count')}
                                                _hover={{ bg: 'gray.50' }}
                                            >
                                                <HStack>
                                                    <Text>Customer Count</Text>

                                                </HStack>
                                            </Th>
                                            <Th
                                                cursor="pointer"
                                                onClick={() => handleSort('status')}
                                                _hover={{ bg: 'gray.50' }}
                                            >
                                                <HStack>
                                                    <Text>Status</Text>
                                                    {getSortIcon('status')}
                                                </HStack>
                                            </Th>


                                            <Th>Refresh Rate</Th>
                                            <Th
                                                cursor="pointer"
                                                onClick={() => handleSort('updated_at')}
                                                _hover={{ bg: 'gray.50' }}
                                            >
                                                <HStack>
                                                    <Text>Last Updated</Text>
                                                    {/* {getSortIcon('updated_at')} */}
                                                </HStack>
                                            </Th>
                                            <Th textAlign={"center"}>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {cohorts.map((cohort) => (
                                            <Tr key={cohort.id} _hover={{ bg: 'gray.50' }}>
                                                <Td>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="semibold">{cohort.name}</Text>
                                                        <Text fontSize="xs" color="gray.500">Created by {cohort?.createdBy}</Text>
                                                    </VStack>
                                                </Td>
                                                <Td maxW="360px">
                                                    <Text fontSize="sm" whiteSpace="normal" wordBreak="break-word">
                                                        {cohort.description || 'No description'}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Text fontWeight="semibold">
                                                        {cohort.customerCount.toLocaleString()}
                                                    </Text>
                                                </Td>
                                                <Td display={"flex"} justifyContent={"center"} alignItems={"center"} paddingStart={0}>
                                                    <VStack >


                                                        <CustomBadge
                                                            px={2}
                                                            py={.8}
                                                            borderRadius="full"
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            color={cohort.status === 'Active' ? '#016630' : '#894b00'}
                                                            bg={cohort.status === 'Active' ? '#dcfce7' : '#fef9c2'}

                                                        >
                                                            {cohort.status}
                                                        </CustomBadge>
                                                        {changeStatusPermission && (

                                                            <Switch
                                                                size="sm"
                                                                isChecked={cohort.status === 'Active'}
                                                                onChange={() => handleStatusToggle(cohort)}
                                                                isDisabled={updatingStatus[cohort.id]}
                                                                colorScheme="green"
                                                            />
                                                        )}

                                                    </VStack>
                                                </Td>


                                                <Td>
                                                    <Badge variant="outline">{cohort.refreshRate}</Badge>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" color="black.600">

                                                        {moment
                                                            .utc(cohort.updatedAt)
                                                            .tz(getUserTimeZone())
                                                            .format("DD MMM YY hh:mm:ss A")}
                                                    </Text>
                                                </Td>
                                                <Td display={"flex"} justifyContent={"center"} alignItems={"center"} py={10} >

                                                    <HStack spacing={2}>
                                                        {pausePermission && (

                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="green"
                                                                onClick={() => {
                                                                    setSelectedCohort(cohort);
                                                                    onOpen();
                                                                }}
                                                                title="Run Again"
                                                            >
                                                                <FiPlay />
                                                            </Button>
                                                        )}
                                                        {reportPermission && (

                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleNavigateReport(cohort)}
                                                            >
                                                                <LuFileText />
                                                            </Button>
                                                        )}

                                                        {editPermission && (

                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleEdit(cohort)}
                                                            >
                                                                <EditIcon />
                                                            </Button>
                                                        )}
                                                        {viewPermission && (

                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleViewCohort(cohort)}
                                                            >
                                                                <BsEye />
                                                            </Button>
                                                        )}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>

                            {/* Loading overlay for pagination */}
                            {isLoading && cohorts.length > 0 && (
                                <Box
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255,255,255,0.8)"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    zIndex="1"
                                >
                                    <Spinner size="lg" color="blue.500" />
                                </Box>
                            )}

                            {/* Pagination - Updated for react-js-pagination */}
                            {cohorts.length !== 0 && (
                                <Box mt={6} display="flex" justifyContent="center">

                                    <Pagination
                                        activePage={currentPage}
                                        itemsCountPerPage={limit}
                                        totalItemsCount={totalCount}
                                        pageRangeDisplayed={5}
                                        onChange={handlePageChange}
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        activeClass="active"
                                        activeLinkClass="active-link"


                                        disabledClass="disabled"

                                    />
                                </Box>
                            )}



                            {/* Empty States */}
                            {cohorts.length === 0 && !isLoading && (
                                <Box textAlign="center" py={10}>
                                    <Text color="gray.500" mb={4}>
                                        {searchTerm || statusFilter
                                            ? 'No segments found matching your criteria'
                                            : 'No segments found'
                                        }
                                    </Text>
                                    {searchTerm || statusFilter ? (
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setSearchTerm('');
                                                setStatusFilter('');
                                            }}
                                        >
                                            Clear Filters
                                        </Button>
                                    ) : (
                                        createPermission && (
                                        <Button
                                            leftIcon={<FiPlus />}
                                            colorScheme="blue"
                                            onClick={handleCreateNew}
                                        >
                                            Create Your First Segment
                                        </Button>
                                        )
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </> : <BoxWrapper>
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </BoxWrapper>}



            </BoxWrapper>

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Run Segment Again
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to run "{selectedCohort?.name}" again?
                            This will reprocess all users in the segment.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="green"
                                onClick={handleTriggerCohort}
                                ml={3}
                                isLoading={isTriggering}
                            >
                                Run Again
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </>
    );
};

export default Cohort;